import React from 'react';
import { Ticket } from '@/types/';
import TicketListItem from './TicketListItem';
import { useSearchParams } from 'react-router';
import { useTabNavigation } from '@/hooks/useTabNavigation';
import { useMobile } from '@/hooks/useMobile';
import { useSelectionStore } from '@/stores/selectionStore';
import { SystemLayerKey } from '@packages/database/types';
import { useShallow } from 'zustand/react/shallow';
import { TicketPagination } from './TicketPagination';

interface TicketListProps {
  tickets: Ticket[];
  // Pagination props passed from parent (already available from useTickets)
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
  isFetching: boolean;
}

const TicketList: React.FC<TicketListProps> = ({
  tickets,
  total,
  totalPages,
  hasNext,
  hasPrev,
  isFetching,
}) => {
  const [, setSearchParams] = useSearchParams();
  const [, setActiveTab] = useTabNavigation('tab');
  const { isMobile } = useMobile();

  const selectedId = useSelectionStore(
    useShallow((s) => (s.selected.length === 1 ? s.selected[0]?.id : null)),
  );
  const setSelection = useSelectionStore((s) => s.setSelection);

  const handleTicketSelect = (item: Ticket) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set('ticketId', item.id);
        return p;
      },
      { replace: true },
    );

    setSelection(
      [{ id: item.id, data: item as any, layerKey: SystemLayerKey.TICKET }],
      'list',
    );

    if (isMobile) setActiveTab('map');
  };

  if (!tickets) return null;

  return (
    <div className="h-full flex flex-col">
      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {tickets.length > 0 ? (
          tickets.map((ticket) => (
            <TicketListItem
              key={ticket.id}
              ticket={ticket}
              selected={selectedId === ticket.id}
              onClick={() => handleTicketSelect(ticket)}
            />
          ))
        ) : (
          <div className="p-6 text-center text-muted-foreground text-sm">
            No tickets found matching your criteria.
          </div>
        )}
      </div>

      <TicketPagination
        total={total}
        totalPages={totalPages}
        hasNext={hasNext}
        hasPrev={hasPrev}
        isFetching={isFetching}
      />
    </div>
  );
};

export default TicketList;
